import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ChevronLeft, ChevronRight, X } from 'lucide-react'; 
import ProductReviewsFeed from '../components/ProductReviewsFeed'; // 🌟 MOUNTED REVIEWS MODULE FEED

const ProductDetail = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [addedStatus, setAddedStatus] = useState('');
  const [activeTab, setActiveTab] = useState('description'); 
  const [currentImgIndex, setCurrentImgIndex] = useState(0); 

  // Tracks if the floating size guide modal is open or closed
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false); 

  const { addToCart, toggleWishlist, wishlist } = useCart(); 

  // Helper function to check if a user token exists in local storage
  const isUserAuthenticated = () => {
    const token = localStorage.getItem('modische_token') || 
                  localStorage.getItem('modische_auth_token') || 
                  localStorage.getItem('token');
    return !!token;
  };

  useEffect(() => {
    fetch(`https://api.modischekledings.com/api/products`)
      .then(res => res.json())
      .then(data => {
        const foundItem = data.find(item => item._id === id);
        if (foundItem) {
          setProduct(foundItem);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error retrieving detailed product:", err);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center text-xs tracking-widest text-[#664229] uppercase font-mono">
        Loading Editorial View...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex flex-col items-center justify-center gap-4 text-xs tracking-widest text-[#664229] uppercase font-mono">
        <p>Piece not found in collection inventory.</p>
        <Link to="/" className="border-b border-[#664229] pb-1 font-bold">Return to Store</Link>
      </div>
    );
  }

  const imgList = product.images && product.images.length > 0 ? product.images : [''];

  const handlePrevImage = () => {
    setCurrentImgIndex((prevIdx) => (prevIdx === 0 ? imgList.length - 1 : prevIdx - 1));
  };

  const handleNextImage = () => {
    setCurrentImgIndex((prevIdx) => (prevIdx === imgList.length - 1 ? 0 : prevIdx + 1));
  };

  // 🌟 PROTECTED BAG ADDITION HANDLER
  const handleBagAddition = () => {
    if (!isUserAuthenticated()) {
      alert('Please sign in to add items to your shopping bag.');
      navigate('/login');
      return;
    }

    if (!selectedSize) {
      setAddedStatus("⚠️ Please select a size first");
      return;
    }
    const success = addToCart(product, selectedSize);
    if (success) {
      setAddedStatus(`✨ Added size ${selectedSize} to your bag!`);
      setTimeout(() => setAddedStatus(''), 3000);
    }
  };

  // 🌟 PROTECTED WISHLIST HANDLER
  const handleWishlistToggle = () => {
    if (!isUserAuthenticated()) {
      alert('Please sign in to add items to your wishlist.');
      navigate('/login');
      return;
    }

    toggleWishlist(product);
  };

  const isWishlisted = wishlist.some(item => item._id === product._id);

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-40 pb-20 px-6 max-w-7xl mx-auto">
      <Link to="/" className="text-[10px] uppercase tracking-[0.3em] font-black text-[#F5F5DC] bg-[#2C1E12] px-4 py-2 mb-12 rounded-sm shadow-md inline-flex items-center gap-2 transition hover:bg-[#664229]">
        <span>←</span> <span>Return Home</span>
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        
        {/* LEFT COLUMN: SLIDER GALLERY */}
        <div className="space-y-4">
          <div className="relative aspect-[3/4] overflow-hidden bg-[#E5D3B3] rounded-sm shadow-sm group">
            <img src={imgList[currentImgIndex]} alt="" className="w-full h-full object-cover" />

            {imgList.length > 1 && (
              <>
                <button type="button" onClick={handlePrevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-[#F5F5DC]/80 backdrop-blur-sm text-[#2C1E12] rounded-full shadow-md md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 hover:bg-[#2C1E12] hover:text-[#F5F5DC] z-30 border-none cursor-pointer">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button type="button" onClick={handleNextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-[#F5F5DC]/80 backdrop-blur-sm text-[#2C1E12] rounded-full shadow-md md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 hover:bg-[#2C1E12] hover:text-[#F5F5DC] z-30 border-none cursor-pointer">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <span className="absolute bottom-4 right-4 text-[9px] uppercase font-mono tracking-widest bg-[#2C1E12]/80 text-[#F5F5DC] px-2 py-1 rounded-sm z-30">
                  {currentImgIndex + 1} / {imgList.length}
                </span>
              </>
            )}
          </div>

          {imgList.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {imgList.map((img, idx) => (
                <button key={idx} onClick={() => setCurrentImgIndex(idx)} className={`aspect-[3/4] overflow-hidden bg-[#E5D3B3] rounded-sm transition border cursor-pointer ${currentImgIndex === idx ? 'border-[#2C1E12] opacity-100' : 'border-transparent opacity-50'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: INFORMATION LAYOUT */}
        <div className="flex flex-col justify-start py-2 text-left">
          <div>
            <span className="text-[9px] uppercase tracking-[0.25em] text-[#664229] opacity-60 font-bold block mb-2">
              {product.gender} — {product.category}
            </span>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#2C1E12] uppercase mb-4 leading-tight">
              {product.name}
            </h1>
            
            <div className="text-lg font-medium mb-8">
              {product.discountPrice ? (
                <p>
                  <span className="line-through text-red-700/60 mr-3">₹{product.price}</span>
                  <span className="text-[#2C1E12] font-bold text-xl">₹{product.discountPrice}</span>
                </p>
              ) : (
                <p className="text-[#2C1E12] font-bold">₹{product.price}</p>
              )}
            </div>

            {/* SIZING MODULE */}
            <div className="border-t border-[#664229]/10 pt-6 mb-8">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-[10px] uppercase tracking-widest text-[#664229] font-bold">Select Fit Size</h4>
                
                <button 
                  type="button"
                  onClick={() => setIsSizeGuideOpen(true)}
                  className="text-[9px] uppercase text-[#664229]/60 font-mono tracking-wider cursor-pointer hover:underline hover:text-[#2C1E12] bg-transparent border-none"
                >
                  Size Chart Guide
                </button>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {product.sizes && product.sizes.map(size => (
                  <button key={size} onClick={() => { setSelectedSize(size); setAddedStatus(''); }}
                    className={`w-12 h-12 text-xs font-mono border transition rounded-sm flex items-center justify-center cursor-pointer ${selectedSize === size ? 'bg-[#2C1E12] text-[#F5F5DC] border-transparent' : 'border-[#664229]/20 text-[#2C1E12] hover:border-[#2C1E12]'}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ACTION CONSOLES */}
          <div className="space-y-3 mb-10">
            <button onClick={handleBagAddition} className="w-full py-5 bg-[#2C1E12] text-[#F5F5DC] text-[10px] uppercase tracking-[0.3em] font-bold hover:opacity-90 transition shadow-md border-none cursor-pointer">
              Add to Bag
            </button>
            <button onClick={handleWishlistToggle} className={`w-full py-4 border text-[10px] uppercase tracking-widest font-bold transition cursor-pointer ${isWishlisted ? 'bg-red-800 text-white border-transparent' : 'border-[#2C1E12]/30 text-[#2C1E12]'}`}>
              {isWishlisted ? "❤️ In Your Wishlist" : "🖤 Add to Wishlist"}
            </button>
            {addedStatus && <p className="text-center text-xs font-medium tracking-wide text-[#664229] mt-2 animate-pulse">{addedStatus}</p>}
          </div>

          {/* EDITORIAL CONTENT TAB PANELS */}
          <div className="border-t border-[#664229]/10 pt-6">
            <div className="flex space-x-6 border-b border-[#664229]/10 pb-3 mb-4 overflow-x-auto whitespace-nowrap scrollbar-none">
              <button onClick={() => setActiveTab('description')}
                className={`text-[10px] uppercase tracking-widest font-bold pb-1 transition-all bg-transparent border-none cursor-pointer ${activeTab === 'description' ? 'text-[#2C1E12] border-b-2 border-[#2C1E12]' : 'text-[#664229]/50'}`}>
                Description
              </button>
              <button onClick={() => setActiveTab('details')} 
                className={`text-[10px] uppercase tracking-widest font-bold pb-1 transition-all bg-transparent border-none cursor-pointer ${activeTab === 'details' ? 'text-[#2C1E12] border-b-2 border-[#2C1E12]' : 'text-[#664229]/50'}`}>
                Details
              </button>
              <button onClick={() => setActiveTab('shipping')}
                className={`text-[10px] uppercase tracking-widest font-bold pb-1 transition-all bg-transparent border-none cursor-pointer ${activeTab === 'shipping' ? 'text-[#2C1E12] border-b-2 border-[#2C1E12]' : 'text-[#664229]/50'}`}>
                Shipping & Returns
              </button>
            </div>

            <div className="min-h-[200px]">
              {activeTab === 'description' && (
                <p className="text-xs text-[#2C1E12]/80 leading-relaxed font-serif max-w-md animate-fade-in">
                  {product.description || 'No descriptive story added for this piece.'}
                </p>
              )}
              
              {activeTab === 'details' && (
                <p className="text-xs text-[#2C1E12]/80 leading-relaxed font-serif max-w-md bg-[#E5D3B3]/20 p-4 border-l-2 border-[#2C1E12] italic animate-fade-in">
                  {product.details || 'Premium formulation construction elements.'}
                </p>
              )}

              {activeTab === 'shipping' && (
                <div className="text-xs text-[#2C1E12]/90 leading-relaxed font-serif max-w-md space-y-6 animate-fade-in overflow-y-auto max-h-[350px] pr-2 scrollbar-thin">
                  <div>
                    <h5 className="text-[10px] uppercase tracking-wider text-[#664229] font-bold font-sans mb-1">Return & Exchange Guidelines</h5>
                    <p className="mb-2 text-zinc-500 italic text-[11px]">We proudly offer a premium, hassle-free policy. Requests must be submitted within 3 days of delivery tracking logs and are accepted under all conditions:</p>
                    <ul className="list-disc pl-4 space-y-1 text-[#2C1E12]/80">
                      <li><strong>No Questions Asked:</strong> Fit variations, quality preferences, or change of mind claims are fully accepted for exchanges or platform credits.</li>
                      <li>Wrong product or incorrect size dispatched due to internal catalog error.</li>
                      <li>Damaged, defective, or structurally compromised garment items received.</li>
                      <li>Missing items or quantitative package inventory mismatch.</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-[10px] uppercase tracking-wider text-[#664229] font-bold font-sans mb-1">Item Conditions & Proof Clauses</h5>
                    <ul className="list-disc pl-4 space-y-1 text-[#2C1E12]/80">
                      <li>Garments must remain completely unused, unwashed, and in original packaging.</li>
                      <li>All structural brand tags, collection labels, and tags must remain intact.</li>
                      <li><span className="text-red-950 font-bold font-sans tracking-wide">⚠️ CRITICAL REQUIREMENT:</span> For claims regarding wrong products, damages, or missing packages, a continuous, unedited unboxing video record is strictly required by logistics adjusters. Up to 6 verification photos can be loaded into your user history profile desk.</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-[10px] uppercase tracking-wider text-red-800 font-bold font-sans mb-1">Non-Returnable Items</h5>
                    <ul className="list-disc pl-4 space-y-1 text-zinc-600">
                      <li>Strictly customized tailoring requests, clearout archive sales, or modified bespoke premium garments.</li>
                      <li>Bulk wholesale manufacturing purchase rows.</li>
                      <li>Washed, altered, worn, or customer-damaged textiles.</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-[10px] uppercase tracking-wider text-[#664229] font-bold font-sans mb-1">How to File Requests</h5>
                    <p>Log into your personal account dashboard, head to the Order History portal page, select your target transaction card row within 3 days, pick your reason modifier, and attach your verification assets smoothly.</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 bg-[#E5D3B3]/20 p-3 rounded-sm border-l border-[#664229]/20">
                    <div>
                      <h6 className="text-[9px] uppercase tracking-widest text-[#2C1E12] font-bold mb-0.5">Exchange Process</h6>
                      <p className="text-[11px]">Subject to instant showroom stock allocation. Replacements clear production lines immediately following arrival inspection audits at our dispatch hub.</p>
                    </div>
                    <div>
                      <h6 className="text-[9px] uppercase tracking-widest text-[#2C1E12] font-bold mb-0.5">Refund Settlement</h6>
                      <p className="text-[11px]">Approved adjustments process smoothly within 7–10 working business days directly back into your original automated payment gateway routing account channel.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* APPAREL SIZE GUIDE CHART MODAL */}
      {isSizeGuideOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fade-in">
          <div 
            className="absolute inset-0 bg-[#2C1E12]/60 backdrop-blur-sm cursor-pointer" 
            onClick={() => setIsSizeGuideOpen(false)} 
          />

          <div className="relative w-full max-w-xl bg-[#F5F5DC] text-[#2C1E12] p-8 rounded-sm shadow-2xl border border-[#E5D3B3] z-10 animate-scale-up text-left">
            <button 
              onClick={() => setIsSizeGuideOpen(false)}
              className="absolute top-6 right-6 p-1 text-[#2C1E12] opacity-50 hover:opacity-100 transition bg-transparent border-none cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-6 border-b border-[#664229]/10 pb-4">
              <h3 className="text-xl font-bold tracking-tight text-[#2C1E12] uppercase">Garment Fit Chart</h3>
              <p className="text-[9px] uppercase tracking-widest text-[#664229] opacity-70 mt-0.5">
                Standard Measurements in Inches (Tolerances ±0.5")
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-mono">
                <thead>
                  <tr className="bg-[#2C1E12] text-[#F5F5DC] uppercase tracking-wider text-[10px]">
                    <th className="p-3">Size Tag</th>
                    <th className="p-3">Chest Circumference</th>
                    <th className="p-3">Front Length</th>
                    <th className="p-3">Sleeve Length</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#664229]/10">
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">XS</td>
                    <td className="p-3 text-[#664229]">36" - 38"</td>
                    <td className="p-3 text-[#664229]">26.5"</td>
                    <td className="p-3 text-[#664229]">7.5"</td>
                  </tr>
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">S</td>
                    <td className="p-3 text-[#664229]">38" - 40"</td>
                    <td className="p-3 text-[#664229]">27.5"</td>
                    <td className="p-3 text-[#664229]">8.0"</td>
                  </tr>
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">M</td>
                    <td className="p-3 text-[#664229]">40" - 42"</td>
                    <td className="p-3 text-[#664229]">28.5"</td>
                    <td className="p-3 text-[#664229]">8.5"</td>
                  </tr>
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">L</td>
                    <td className="p-3 text-[#664229]">42" - 44"</td>
                    <td className="p-3 text-[#664229]">29.5"</td>
                    <td className="p-3 text-[#664229]">9.0"</td>
                  </tr>
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">XL</td>
                    <td className="p-3 text-[#664229]">44" - 46"</td>
                    <td className="p-3 text-[#664229]">30.5"</td>
                    <td className="p-3 text-[#664229]">9.5"</td>
                  </tr>
                  <tr className="hover:bg-[#E5D3B3]/10">
                    <td className="p-3 font-bold bg-[#E5D3B3]/20">XXL</td>
                    <td className="p-3 text-[#664229]">46" - 48"</td>
                    <td className="p-3 text-[#664229]">31.5"</td>
                    <td className="p-3 text-[#664229]">10.0"</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 p-4 bg-[#E5D3B3]/30 text-[11px] font-serif italic text-[#664229] border-l-2 border-[#2C1E12]">
              * For an **Oversized Fit** silhouette collection item, we have already added 2.5 extra inches onto the pattern drop defaults. Purchase your normal true standard baseline size.
            </div>
          </div>
        </div>
      )}

      {/* RENDER DYNAMIC PUBLIC CLIENT REVIEWS FEED MODULATOR */}
      <ProductReviewsFeed productId={id} />

    </div>
  );
};

export default ProductDetail;